<template>
  <settings-form :breadcrumbs="useSettingsBreadcrumbs()">
    <template #actions>
      <v-spacer />
      <v-checkbox
        v-model="showInactive"
        :label="$t('button.show_inactive')"
        class="mr-8 mt-0 pt-0"
        hide-details
        :dense="true"
      />
      <v-btn
        class="mr-2"
        color="primary"
        @click="edit"
      >
        <v-icon>mdi-plus</v-icon>
        {{ $t('button.add_new_service') }}
      </v-btn>
    </template>
    <template #content>
      <v-data-table
        v-reorder-table
        class="clickable-table"
        :headers="headers"
        :items="services"
        disable-pagination
        disable-sort
        mobile-breakpoint="0"
        hide-default-footer
        @click:row="edit"
        @sorted="changeOrder"
      >
        <template #item.id>
          <div class="reorder-handle w-100">
            <v-icon small>
              mdi-arrow-up-down
            </v-icon>
          </div>
        </template>
        <template #item.imageUrl="{item}">
          <v-img
            class="px-0 mx-0"
            contain
            max-width="50"
            max-height="38"
            :src="displayImageUrl(item)"
          />
        </template>

        <template #item.color="{item}">
          <v-icon :color="item.color">
            mdi-circle
          </v-icon>
        </template>

        <template #item.title="{item}">
          {{ displayTitle(item) }}
        </template>

        <template #item.minPartySize="{item}">
          {{ item.minPartySize }} - {{ item.maxPartySize }}
        </template>

        <template #item.duration="{item}">
          {{ item.duration === undefined ? '' : localized.durationFromSeconds(item.duration * 60) }}
        </template>

        <template #item.localStartDate="{item}">
          {{ item.localStartTime }}
        </template>

        <template #item.localEndDate="{item}">
          {{ item.localEndTime }}
        </template>

        <template #item.version="{item}">
          {{ item.translations.length ?? 0 > 0 ? 'Multi Lang' : 'Legacy' }}
        </template>
        <template #item.locale="{item}">
          {{ displayLanguage(item) }}
        </template>

        <template #item.weekDays="{item}">
          <span
            v-for="w, i in localized.weekdays()"
            :key="i"
          >
            <v-chip
              class="px-1 mx-1 white--text"
              label
              :color="item.weekDays?.includes(w.value === 0 ? 7 : w.value ) ? 'green' : 'grey'"
              filled
              x-small
            >{{ w.text }}</v-chip>
          </span>
        </template>

        <template #item.isActive="{item}">
          <v-chip
            :color="colorForServiceStatus(item)"
            small
          >
            {{ labelForServiceStatus(item) }}
          </v-chip>
        </template>
        <template #item.actions="{item}">
          <v-menu y-offset>
            <template #activator="{ on }">
              <v-btn
                icon
                v-on="on"
              >
                <v-icon small>
                  mdi-dots-vertical
                </v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="duplicateService(item)">
                <v-list-item-title>
                  <v-icon
                    small
                    class="pr-2"
                  >
                    mdi-content-duplicate
                  </v-icon>
                  <span class="text-button">{{ $t('button.create_copy') }}</span>
                </v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="deleteConfirmation(item)">
                <v-list-item-title>
                  <v-icon
                    small
                    class="pr-2"
                  >
                    mdi-delete
                  </v-icon>
                  <span class="text-button">{{ $t('button.delete') }}</span>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </template>
  </settings-form>
</template>

<script setup lang="ts">
import {
  computed, getCurrentInstance, onBeforeUnmount, onMounted, ref,
  watch,
} from 'vue';
import { useSettingsBreadcrumbs } from '@/mixins/settings-breadcrumbs';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import Service from '@/model/Service';
import i18n from '@/plugins/i18n';
import localized from '@/plugins/vue-localized-formatter/src/localized';
import Sortable from 'sortablejs';
import { cloneModel } from '@/model/model-utils';
import { performDispatchAction, performSaveAction } from '@/services/vue-utils';
import { loadStoreModule, unloadStoreModule } from '@/services/module-loader';
import ServiceManagerStore from '@/store/service-manager-store';
import ReservationService from '@/grpc-api/model/reservation-service';
import store from '@/store/store';
import { useRouter } from 'vue-router/composables';
import Locale from '@/model/Locale';
import { useVuetify } from '../helpers/vue2-helpers';
import showConfirmationDialog from '../dialogs/confirmation-dialog';

const vuetify = useVuetify();
const router = useRouter();

const IS_SVCMGR_INTERNAL_BUILD = false;

const headers = computed(
  () => [
    {
      text: '', width: '32', value: 'id', align: 'center',
    },
    {
      text: i18n.t('label.service_manager_image'),
      width: '50',
      value: 'imageUrl',
      align: 'center',
    },
    {
      text: i18n.t('label.color'), width: '24', value: 'color', align: 'center', cellClass: 'mx-1',
    },
    {
      text: i18n.t('label.service_title'), value: 'title', align: 'start', width: '100%',
    },
    ...vuetify.breakpoint.lgAndUp
      ? [{
        text: i18n.t('label.weekdays'), value: 'weekDays', align: 'start', cellClass: 'dt-cell-stretch',
      },
      ...IS_SVCMGR_INTERNAL_BUILD ? [
        {
          text: i18n.t('label.language'), value: 'locale', align: 'start', cellClass: 'dt-cell-stretch',
        },
        {
          text: 'Version', value: 'version', align: 'start', cellClass: 'dt-cell-stretch',
        },
      ] : [],
      ] : [],
    ...vuetify.breakpoint.mdAndUp
      ? [{
        text: i18n.t('label.service_time_from'), value: 'localStartDate', align: 'center', width: '32',
      },
      {
        text: i18n.t('label.service_time_until'), value: 'localEndDate', align: 'center', width: '32',
      }] : [],
    {
      text: i18n.t('label.active'), width: '32', value: 'isActive', align: 'start', sortable: true,
    },
    { value: 'actions', width: '32' },
  ],
);

const colorForServiceStatus = (s: ReservationService) => {
  if (!s.isActive) return 'gray';
  if (s.tabs.length > 0 && s.tabs.every((t) => !t.usingWeekdaysAndTimes)) { return 'red'; }
  return 'success';
};
const labelForServiceStatus = (s: ReservationService) => {
  if (!s.isActive) return i18n.t('label.inactive');
  if (s.tabs.length > 0 && s.tabs.every((t) => !t.usingWeekdaysAndTimes)) { return i18n.t('label.offline'); }
  return i18n.t('label.active');
};

const services = ref([] as ReservationService[]);

const edit = (s: Service | undefined = undefined) => router.push(`/settings/service-manager/${s?.id || 'new'}`);

const showInactive = ref(false);

const reset = async () => {
  const entities = store.getters.serviceManagertServices as ReservationService[];
  services.value = entities.map((s) => cloneModel(s)).filter((s) => (showInactive.value || s.isActive));
};

const load = async () => {
  if (!store.getters.isServiceManagerLoaded) {
    await performDispatchAction('loadServiceManager', {}, { streamLoad: true }) as ReservationService[] | null;
  }
  reset();
};

watch(showInactive, () => reset());

const changeOrder = async (event: Sortable.SortableEvent) => {
  if (event.oldIndex === undefined || event.newIndex === undefined) return;
  const movedItem = services.value.splice(event.oldIndex, 1)[0];
  services.value.splice(event.newIndex, 0, movedItem);

  const saveItem = cloneModel(movedItem);
  if (event.newIndex === 0)saveItem.order = 0; else saveItem.order = services.value[event.newIndex - 1].order;
  await performSaveAction(
    undefined,
    async () => store.dispatch('serviceManagerReorderServices', { services: [saveItem] }),
  );
  reset();
};

const duplicateService = (s: Service) => {
  router.push(`/settings/service-manager/${s.id}?action=copy`);
};

const deleteService = async (svc: ReservationService) => {
  const s = cloneModel(svc);
  s.isDeleted = true;
  const ok = await performSaveAction(
    undefined,
    async () => store.dispatch('serviceManagerUpdateService', { service: s }),
  );
  reset();
};

const deleteConfirmation = (svc: ReservationService) => {
  showConfirmationDialog(
    i18n.tc('title.service_manager'),
    i18n.tc('message.confirm_delete_service'),
    { title: i18n.tc('button.yes'), action: () => deleteService(svc) },
    { title: i18n.tc('button.cancel') },
  );
};

onMounted(() => {
  loadStoreModule(getCurrentInstance()?.proxy!, 'serviceManager', ServiceManagerStore, async () => { load(); });
});

onBeforeUnmount(() => {
  console.log('serviceManager onBeforeUnmount');

  unloadStoreModule(
    getCurrentInstance()?.proxy!,
    'serviceManager',
    async () => { store.dispatch('resetServiceManager'); },
  );
});

// display fields
const localeToCountry = (locale: string | undefined) => (
  locale ? i18n.t(`code.locale.${locale}`) : undefined
);

const defaultTranslation = (s: ReservationService) => {
  const defaultLocale = store.getters.accountLocale as string;

  return s.translations.find((t) => t.locale === defaultLocale)
    || s.translations.find((t) => t.locale === 'en')
    || s.translations[0];
};

const displayImageUrl = (s: ReservationService) => defaultTranslation(s)?.imageUrl || s.imageUrl;

const displayTitle = (s: ReservationService) => defaultTranslation(s)?.title || s.title;

const displayLanguage = (s: ReservationService) => {
  if (s.translations.length === 0) return localeToCountry(s.locale);
  if (s.translations.length === 1) return localeToCountry(s.translations[0].locale);

  const enabledLocales = store.getters.enabledLocales as string[];
  const translatedLocales = enabledLocales.filter((el) => s.translations.some((t) => t.locale === el));
  console.log('enabledLocales', enabledLocales);

  if (translatedLocales.length === 1) return localeToCountry(translatedLocales[0]);
  if (translatedLocales.length === 2) return translatedLocales.map((tl) => localeToCountry(tl)).join(',');
  if (translatedLocales.length > 2) return translatedLocales.map((tl) => tl.toUpperCase()).join(',');

  return undefined;
};

</script>
